import { useState, type ReactNode } from "react";
import { ArrowRight, Bell, ChevronDown, ChevronRight, ListChecks, ListTodo } from "lucide-react";
import { useCompleteTask, useCreateEvent, useTasks } from "../api/hooks";
import type { CalEvent } from "../api/types";
import { MONTHS_SHORT, WEEKDAYS_SHORT, addDays, datePart, formatTime, minutesOf, parts, weekday, type DateStr } from "../lib/date";
import { draftToInput, newDraft } from "../lib/draft";
import { anchorOf, useUi } from "../state/ui";
import { TaskChart } from "./TaskChart";
import { useToResolutionWithUndo } from "./overlays/EventDetails";
import "./pages.css";

/** "Hoje", "Amanhã 14:00", "Ontem", "qua., 15 de out." */
export function taskWhen(e: CalEvent, today: DateStr): string {
  const day = datePart(e.start);
  const time = e.allDay ? "" : ` ${formatTime(minutesOf(e.start))}`;
  if (day === today) return `Hoje${time}`;
  if (day === addDays(today, 1)) return `Amanhã${time}`;
  if (day === addDays(today, -1)) return `Ontem${time}`;
  const { y, m, d } = parts(day);
  return `${WEEKDAYS_SHORT[weekday(day)]}, ${d} de ${MONTHS_SHORT[m - 1]}${y !== parts(today).y ? ` de ${y}` : ""}${time}`;
}

/** Due before today (an all-day task is due by the end of its day). */
export function isOverdue(e: CalEvent, today: DateStr): boolean {
  return datePart(e.start) < today;
}

/** Page header: eyebrow, display title, one line of context. */
export function PageHead({ label, title, children }: { label: string; title: ReactNode; children?: ReactNode }) {
  return (
    <header className="page-head">
      <span className="eyebrow">{label}</span>
      <h1 className="page-title display">{title}</h1>
      {children && <p className="page-lead">{children}</p>}
    </header>
  );
}

/** Rule-separated list with an eyebrow header and count. */
export function RowSection({ title, count, tone, children }: { title: string; count: number; tone?: "danger"; children: ReactNode }) {
  return (
    <section className="row-section">
      <h2 className={`row-section-title eyebrow${tone ? ` ${tone}` : ""}`}>
        {title} <span>{String(count).padStart(2, "0")}</span>
      </h2>
      <ul className="rows">{children}</ul>
    </section>
  );
}

function readOpen(key?: string): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/** Eyebrow toggle with a chevron; `storageKey` remembers it open in this browser. */
export function CollapsibleSection({ title, count, storageKey, children }: { title: string; count?: number; storageKey?: string; children: ReactNode }) {
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const toggle = () =>
    setOpen((o) => {
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, o ? "0" : "1");
        } catch {
          // Only the remembered state is lost.
        }
      }
      return !o;
    });
  return (
    <section className="row-section">
      <button className="row-section-title eyebrow row-toggle" onClick={toggle} aria-expanded={open}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {title} {count !== undefined && <span>{String(count).padStart(2, "0")}</span>}
      </button>
      {open && children}
    </section>
  );
}

/** Collapsible "done" section. */
export function DoneSection({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (!count) return null;
  return (
    <CollapsibleSection title={title} count={count}>
      <ul className="rows">{children}</ul>
    </CollapsibleSection>
  );
}

/** Small marker telling a task from a reminder (appointments go unmarked). */
export function KindMark({ kind }: { kind: CalEvent["kind"] }) {
  if (kind === "event") return null;
  const label = kind === "task" ? "Tarefa" : "Lembrete";
  return (
    <span className="kind-mark" title={label}>
      {kind === "task" ? <ListChecks size={13} aria-hidden /> : <Bell size={13} aria-hidden />}
      {label}
    </span>
  );
}

export function TaskRow({ task }: { task: CalEvent }) {
  const ui = useUi();
  const complete = useCompleteTask();
  const toResolution = useToResolutionWithUndo();
  const toggle = () => {
    const completed = !task.completed;
    complete.mutate(
      { event: task, completed },
      {
        onSuccess: () =>
          completed &&
          ui.showToast(task.kind === "reminder" ? "Lembrete concluído" : "Tarefa concluída", { label: "Desfazer", run: () => complete.mutate({ event: { ...task, completed: true }, completed: false }) }),
        onError: (err) => ui.showToast(`Não foi possível atualizar: ${(err as Error).message}`)
      }
    );
  };
  const overdue = !task.completed && isOverdue(task, ui.today);
  return (
    <li className={`row${task.completed ? " done" : ""}`}>
      <label className="checkbox round row-check">
        <input type="checkbox" checked={task.completed} onChange={toggle} aria-label={task.completed ? `Reabrir ${task.title}` : `Concluir ${task.title}`} />
      </label>
      <button className="row-main" onClick={(ev) => ui.setOverlay({ kind: "details", event: task, anchor: anchorOf(ev.currentTarget) })}>
        <span className="row-title">{task.title || "(sem título)"}</span>
      </button>
      <span className="row-meta">
        <KindMark kind={task.kind} />
        {overdue && <span className="chip danger">Atrasada</span>}
        <span className="row-when">{taskWhen(task, ui.today)}</span>
        <span className="color-dot" style={{ background: task.color }} />
        {!task.completed && (
          <button className="icon-btn sm row-hover-action" title="Transformar em resolução" aria-label={`Transformar ${task.title} em resolução`} onClick={() => toResolution(task)}>
            <ListTodo size={15} />
          </button>
        )}
      </span>
    </li>
  );
}

/** One-line add: a task for today (all day); "Mais opções" opens the full editor. */
export function TaskQuickAdd() {
  const ui = useUi();
  const create = useCreateEvent();
  const [title, setTitle] = useState("");
  const draft = () => ({
    ...newDraft({ date: ui.today, allDay: true, kind: "task", settings: ui.me.settings, calendars: ui.calendars }),
    title: title.trim()
  });
  return (
    <form
      className="add-line"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        create.mutate(
          { ...draftToInput(draft()), clientRequestId: crypto.randomUUID() },
          { onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`) }
        );
        setTitle("");
      }}
    >
      <input className="add-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nova tarefa para hoje…" aria-label="Nova tarefa" />
      <button type="button" className="btn text add-more" onClick={() => ui.setOverlay({ kind: "editor", draft: draft() })}>
        Mais opções
      </button>
      <button type="submit" className="btn primary add-submit" disabled={!title.trim()} aria-label="Adicionar tarefa">
        <ArrowRight size={16} />
      </button>
    </form>
  );
}

/** "Tarefas pendentes": today's agenda plus tasks and reminders not yet done, grouped by due date. */
export function TasksPage() {
  const ui = useUi();
  const { data, isLoading } = useTasks();
  const pending = data?.pending ?? [];
  const overdue = pending.filter((t) => isOverdue(t, ui.today));
  const today = pending.filter((t) => datePart(t.start) === ui.today);
  const later = pending.filter((t) => datePart(t.start) > ui.today);
  const done = data?.completed ?? [];

  return (
    <div className="page-scroll">
      <div className="page">
        <PageHead label="Tarefas" title="Pendências com data.">
          Tudo o que está na agenda de hoje, rotinas incluídas, suas tarefas e seus lembretes — os que você pede no WhatsApp também aparecem aqui.
        </PageHead>
        <TaskQuickAdd />
        {isLoading ? (
          <div className="center-fill" style={{ height: 200 }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            {!pending.length && <p className="empty">Nada pendente. Aproveite.</p>}
            {overdue.length > 0 && (
              <RowSection title="Atrasadas" count={overdue.length} tone="danger">
                {overdue.map((t) => <TaskRow key={t.key} task={t} />)}
              </RowSection>
            )}
            {today.length > 0 && (
              <RowSection title="Hoje" count={today.length}>
                {today.map((t) => <TaskRow key={t.key} task={t} />)}
              </RowSection>
            )}
            {later.length > 0 && (
              <RowSection title="Próximas" count={later.length}>
                {later.map((t) => <TaskRow key={t.key} task={t} />)}
              </RowSection>
            )}
            <DoneSection title="Concluídas · 7 dias" count={done.length}>
              {done.map((t) => <TaskRow key={t.key} task={t} />)}
            </DoneSection>
            <CollapsibleSection title="Gráfico de concluídas" storageKey="tasks.chartOpen">
              <TaskChart />
            </CollapsibleSection>
          </>
        )}
      </div>
    </div>
  );
}
